import { useTranslation } from "react-i18next";
import { Bot, Loader2 } from "lucide-react";
import type { ToolCallEvent } from "@/hooks/useAgentChat";
import { toolStreamingI18nKey } from "@/lib/chat-utils";

interface StreamingIndicatorProps {
  activeToolCalls?: ToolCallEvent[];
  hasText: boolean;
}

/**
 * Derive the phase text for the streaming indicator based on context:
 * - If active tool calls exist, show the tool-specific label
 * - If no tools and no text yet, show "thinking"
 * - If no tools but text is flowing, show "writing"
 */
function getStreamingPhaseKey(
  activeToolCalls: ToolCallEvent[] | undefined,
  hasText: boolean,
): string {
  if (activeToolCalls && activeToolCalls.length > 0) {
    return toolStreamingI18nKey(activeToolCalls[0].toolName);
  }
  if (!hasText) {
    return "inspectionChat.streaming.thinking";
  }
  return "inspectionChat.streaming.writing";
}

/**
 * Context-aware streaming indicator component.
 *
 * Shows the current streaming phase (thinking, writing, or tool-specific label)
 * alongside an animated indicator. Matches the existing loading indicator pattern
 * from InspectionChat.
 */
export function StreamingIndicator({ activeToolCalls, hasText }: StreamingIndicatorProps) {
  const { t } = useTranslation();
  const phaseKey = getStreamingPhaseKey(activeToolCalls, hasText);
  const phaseText = t(phaseKey);
  const hasActiveTool = activeToolCalls && activeToolCalls.length > 0;

  return (
    <div className="flex gap-3 animate-in fade-in duration-200">
      {/* Bot avatar */}
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {hasActiveTool ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bot className="h-4 w-4" />}
      </div>

      {/* Bubble */}
      <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-2 flex items-center gap-2">
        {hasActiveTool ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            <span className="text-xs font-normal text-muted-foreground">{phaseText}</span>
          </>
        ) : (
          <>
            {/* Animated three-dot indicator */}
            <div className="flex gap-1">
              <span
                className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce"
                style={{ animationDelay: "0ms" }}
              />
              <span
                className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce"
                style={{ animationDelay: "150ms" }}
              />
              <span
                className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce"
                style={{ animationDelay: "300ms" }}
              />
            </div>
            <span className="text-xs font-normal text-muted-foreground">{phaseText}</span>
          </>
        )}
      </div>
    </div>
  );
}
